import { SecurityVulnerability } from '../types';

export interface CodeQLQuery {
  id: string;
  name: string;
  description: string;
  category: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  language: string;
  query: string;
}

export interface CodeQLResult {
  queryId: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  message: string;
  location: {
    file: string;
    line: number;
    column: number;
  };
  description: string;
  cwe?: string;
  cve?: string;
  recommendation: string;
}

export interface CodeQLAnalysis {
  totalIssues: number;
  criticalIssues: number;
  highIssues: number;
  mediumIssues: number;
  lowIssues: number;
  infoIssues: number;
  results: CodeQLResult[];
  coverage: {
    filesScanned: number;
    totalFiles: number;
    languages: string[];
  };
  scanTime: number;
  recommendations: string[];
}

export class CodeQLAnalysisService {
  // Predefined CodeQL queries for common security issues
  private static readonly CODEQL_QUERIES: CodeQLQuery[] = [
    // SQL Injection
    {
      id: 'sql-injection',
      name: 'SQL Injection',
      description: 'Detects potential SQL injection vulnerabilities',
      category: 'injection',
      severity: 'high',
      language: 'javascript',
      query: `
        import javascript
        from SqlInjectionSink sink, SqlInjectionSource source
        where sink.getEnclosingCall().getEnclosingFunction() = source.getEnclosingFunction()
        select sink, source
      `
    },
    
    // XSS (Cross-Site Scripting)
    {
      id: 'xss',
      name: 'Cross-Site Scripting',
      description: 'Detects potential XSS vulnerabilities',
      category: 'injection',
      severity: 'high',
      language: 'javascript',
      query: `
        import javascript
        from XssSink sink, XssSource source
        where sink.getEnclosingCall().getEnclosingFunction() = source.getEnclosingFunction()
        select sink, source
      `
    },
    
    // Command Injection
    {
      id: 'command-injection',
      name: 'Command Injection',
      description: 'Detects potential command injection vulnerabilities',
      category: 'injection',
      severity: 'critical',
      language: 'javascript',
      query: `
        import javascript
        from CommandInjectionSink sink, CommandInjectionSource source
        where sink.getEnclosingCall().getEnclosingFunction() = source.getEnclosingFunction()
        select sink, source
      `
    },
    
    // Path Traversal
    {
      id: 'path-traversal',
      name: 'Path Traversal',
      description: 'Detects potential path traversal vulnerabilities',
      category: 'injection',
      severity: 'high',
      language: 'javascript',
      query: `
        import javascript
        from PathTraversalSink sink, PathTraversalSource source
        where sink.getEnclosingCall().getEnclosingFunction() = source.getEnclosingFunction()
        select sink, source
      `
    },
    
    // Hardcoded Credentials
    {
      id: 'hardcoded-credentials',
      name: 'Hardcoded Credentials',
      description: 'Detects hardcoded passwords, API keys, and tokens',
      category: 'secrets',
      severity: 'high',
      language: 'javascript',
      query: `
        import javascript
        from HardcodedCredential credential
        select credential
      `
    },
    
    // Insecure Random
    {
      id: 'insecure-random',
      name: 'Insecure Random',
      description: 'Detects use of cryptographically weak random number generators',
      category: 'crypto',
      severity: 'medium',
      language: 'javascript',
      query: `
        import javascript
        from InsecureRandom random
        select random
      `
    },
    
    // Weak Crypto
    {
      id: 'weak-crypto',
      name: 'Weak Cryptographic Algorithm',
      description: 'Detects use of weak cryptographic algorithms',
      category: 'crypto',
      severity: 'high',
      language: 'javascript',
      query: `
        import javascript
        from WeakCrypto crypto
        select crypto
      `
    },
    
    // Prototype Pollution
    {
      id: 'prototype-pollution',
      name: 'Prototype Pollution',
      description: 'Detects potential prototype pollution vulnerabilities',
      category: 'injection',
      severity: 'high',
      language: 'javascript',
      query: `
        import javascript
        from PrototypePollutionSink sink, PrototypePollutionSource source
        where sink.getEnclosingCall().getEnclosingFunction() = source.getEnclosingFunction()
        select sink, source
      `
    },
    
    // Unsafe Deserialization
    {
      id: 'unsafe-deserialization',
      name: 'Unsafe Deserialization',
      description: 'Detects unsafe deserialization of user input',
      category: 'injection',
      severity: 'high',
      language: 'javascript',
      query: `
        import javascript
        from UnsafeDeserializationSink sink, UnsafeDeserializationSource source
        where sink.getEnclosingCall().getEnclosingFunction() = source.getEnclosingFunction()
        select sink, source
      `
    },
    
    // Missing CSRF Protection
    {
      id: 'missing-csrf',
      name: 'Missing CSRF Protection',
      description: 'Detects endpoints that may be vulnerable to CSRF attacks',
      category: 'authentication',
      severity: 'medium',
      language: 'javascript',
      query: `
        import javascript
        from MissingCsrfProtection endpoint
        select endpoint
      `
    }
  ];

  /**
   * Perform CodeQL analysis on repository files
   */
  static async analyzeWithCodeQL(
    files: any[],
    repositoryUrl: string,
    language: string = 'javascript'
  ): Promise<CodeQLAnalysis> {
    const startTime = Date.now();
    
    try {
      console.log('Starting CodeQL analysis...');
      
      // Filter files by language
      const relevantFiles = this.filterFilesByLanguage(files, language);
      
      // Perform CodeQL queries
      const results = await this.runCodeQLQueries(relevantFiles, language);
      
      // Analyze results
      const analysis = this.analyzeResults(results, relevantFiles.length, files.length, startTime);
      
      console.log('CodeQL analysis completed:', analysis);
      return analysis;
      
    } catch (error) {
      console.error('Error in CodeQL analysis:', error);
      
      // Return fallback analysis
      return this.getFallbackAnalysis(files.length, startTime);
    }
  }

  /**
   * Run CodeQL queries on the codebase
   */
  private static async runCodeQLQueries(
    files: any[],
    language: string
  ): Promise<CodeQLResult[]> {
    const results: CodeQLResult[] = [];
    
    // Get relevant queries for the language
    const relevantQueries = this.CODEQL_QUERIES.filter(q => q.language === language);
    
    for (const query of relevantQueries) {
      try {
        const queryResults = await this.executeCodeQLQuery(query, files);
        results.push(...queryResults);
      } catch (error) {
        console.warn(`Failed to execute query ${query.id}:`, error);
      }
    }
    
    return results;
  }

  /**
   * Execute a single CodeQL query
   */
  private static async executeCodeQLQuery(
    query: CodeQLQuery,
    files: any[]
  ): Promise<CodeQLResult[]> {
    const results: CodeQLResult[] = [];
    
    // Simulate CodeQL query execution based on query type
    switch (query.id) {
      case 'sql-injection':
        results.push(...this.detectSQLInjection(files));
        break;
      case 'xss':
        results.push(...this.detectXSS(files));
        break;
      case 'command-injection':
        results.push(...this.detectCommandInjection(files));
        break;
      case 'path-traversal':
        results.push(...this.detectPathTraversal(files));
        break;
      case 'hardcoded-credentials':
        results.push(...this.detectHardcodedCredentials(files));
        break;
      case 'insecure-random':
        results.push(...this.detectInsecureRandom(files));
        break;
      case 'weak-crypto':
        results.push(...this.detectWeakCrypto(files));
        break;
      case 'prototype-pollution':
        results.push(...this.detectPrototypePollution(files));
        break;
      case 'unsafe-deserialization':
        results.push(...this.detectUnsafeDeserialization(files));
        break;
      case 'missing-csrf':
        results.push(...this.detectMissingCSRF(files));
        break;
    }
    
    return results;
  }

  /**
   * Detect SQL injection vulnerabilities
   */
  private static detectSQLInjection(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          // Check for SQL injection patterns
          if (
            (line.includes('SELECT') || line.includes('INSERT') || line.includes('UPDATE') || line.includes('DELETE')) &&
            (line.includes('$') || line.includes('+') || line.includes('${')) &&
            (line.includes('req.') || line.includes('params.') || line.includes('body.') || line.includes('query.'))
          ) {
            results.push({
              queryId: 'sql-injection',
              severity: 'high',
              message: 'Potential SQL injection vulnerability detected',
              location: {
                file: file.path,
                line: i + 1,
                column: line.indexOf('$') + 1
              },
              description: 'User input is directly concatenated into SQL query without proper parameterization',
              cwe: 'CWE-89',
              recommendation: 'Use parameterized queries or prepared statements to prevent SQL injection'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect XSS vulnerabilities
   */
  private static detectXSS(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          // Check for XSS patterns
          if (
            (line.includes('innerHTML') || line.includes('outerHTML') || line.includes('document.write')) &&
            (line.includes('req.') || line.includes('params.') || line.includes('body.') || line.includes('query.'))
          ) {
            results.push({
              queryId: 'xss',
              severity: 'high',
              message: 'Potential XSS vulnerability detected',
              location: {
                file: file.path,
                line: i + 1,
                column: line.indexOf('innerHTML') + 1
              },
              description: 'User input is directly inserted into DOM without proper sanitization',
              cwe: 'CWE-79',
              recommendation: 'Sanitize user input and use safe DOM manipulation methods'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect command injection vulnerabilities
   */
  private static detectCommandInjection(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          // Check for command injection patterns
          if (
            (line.includes('exec(') || line.includes('spawn(') || line.includes('system(')) &&
            (line.includes('req.') || line.includes('params.') || line.includes('body.') || line.includes('query.'))
          ) {
            results.push({
              queryId: 'command-injection',
              severity: 'critical',
              message: 'Potential command injection vulnerability detected',
              location: {
                file: file.path,
                line: i + 1,
                column: line.indexOf('exec(') + 1
              },
              description: 'User input is passed to command execution functions without validation',
              cwe: 'CWE-78',
              recommendation: 'Validate and sanitize all user input before passing to command execution functions'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect path traversal vulnerabilities
   */
  private static detectPathTraversal(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          // Check for path traversal patterns
          if (
            (line.includes('fs.readFile') || line.includes('fs.writeFile') || line.includes('path.join')) &&
            (line.includes('req.') || line.includes('params.') || line.includes('body.') || line.includes('query.')) &&
            !line.includes('path.normalize') && !line.includes('path.resolve')
          ) {
            results.push({
              queryId: 'path-traversal',
              severity: 'high',
              message: 'Potential path traversal vulnerability detected',
              location: {
                file: file.path,
                line: i + 1,
                column: line.indexOf('fs.readFile') + 1
              },
              description: 'User input is used in file operations without proper path validation',
              cwe: 'CWE-22',
              recommendation: 'Use path.normalize() and validate file paths before file operations'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect hardcoded credentials
   */
  private static detectHardcodedCredentials(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    const credentialPatterns = [
      { pattern: /api[_-]?key\s*[:=]\s*['"][^'"]+['"]/gi, type: 'API Key' },
      { pattern: /password\s*[:=]\s*['"][^'"]+['"]/gi, type: 'Password' },
      { pattern: /token\s*[:=]\s*['"][^'"]+['"]/gi, type: 'Token' },
      { pattern: /secret\s*[:=]\s*['"][^'"]+['"]/gi, type: 'Secret' },
      { pattern: /AKIA[0-9A-Z]{16}/g, type: 'AWS Access Key' },
      { pattern: /ghp_[a-zA-Z0-9]{36}/g, type: 'GitHub Token' },
      { pattern: /sk_live_[a-zA-Z0-9]{24}/g, type: 'Stripe Secret Key' },
    ];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          for (const { pattern, type } of credentialPatterns) {
            if (pattern.test(line)) {
              results.push({
                queryId: 'hardcoded-credentials',
                severity: 'high',
                message: `Hardcoded ${type} detected`,
                location: {
                  file: file.path,
                  line: i + 1,
                  column: 1
                },
                description: `Hardcoded ${type} found in source code`,
                cwe: 'CWE-259',
                recommendation: `Move ${type} to environment variables or secure configuration management`
              });
            }
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect insecure random number generation
   */
  private static detectInsecureRandom(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          if (line.includes('Math.random()') && !line.includes('crypto.randomBytes')) {
            results.push({
              queryId: 'insecure-random',
              severity: 'medium',
              message: 'Insecure random number generation detected',
              location: {
                file: file.path,
                line: i + 1,
                column: line.indexOf('Math.random()') + 1
              },
              description: 'Math.random() is not cryptographically secure',
              cwe: 'CWE-338',
              recommendation: 'Use crypto.randomBytes() or crypto.getRandomValues() for cryptographic purposes'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect weak cryptographic algorithms
   */
  private static detectWeakCrypto(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    const weakCryptoPatterns = [
      { pattern: /md5/gi, algorithm: 'MD5' },
      { pattern: /sha1/gi, algorithm: 'SHA-1' },
      { pattern: /des/gi, algorithm: 'DES' },
      { pattern: /rc4/gi, algorithm: 'RC4' },
    ];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          for (const { pattern, algorithm } of weakCryptoPatterns) {
            if (pattern.test(line)) {
              results.push({
                queryId: 'weak-crypto',
                severity: 'high',
                message: `Weak cryptographic algorithm ${algorithm} detected`,
                location: {
                  file: file.path,
                  line: i + 1,
                  column: line.toLowerCase().indexOf(algorithm.toLowerCase()) + 1
                },
                description: `${algorithm} is considered cryptographically weak`,
                cwe: 'CWE-327',
                recommendation: `Use stronger algorithms like SHA-256, SHA-512, or AES`
              });
            }
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect prototype pollution vulnerabilities
   */
  private static detectPrototypePollution(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          if (
            (line.includes('Object.assign') || line.includes('Object.defineProperty')) &&
            (line.includes('req.') || line.includes('params.') || line.includes('body.') || line.includes('query.')) &&
            !line.includes('Object.create(null)')
          ) {
            results.push({
              queryId: 'prototype-pollution',
              severity: 'high',
              message: 'Potential prototype pollution vulnerability detected',
              location: {
                file: file.path,
                line: i + 1,
                column: line.indexOf('Object.assign') + 1
              },
              description: 'User input is used in object operations without proper validation',
              cwe: 'CWE-1321',
              recommendation: 'Use Object.create(null) for objects that should not inherit from Object.prototype'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect unsafe deserialization
   */
  private static detectUnsafeDeserialization(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          if (
            (line.includes('eval(') || line.includes('JSON.parse(')) &&
            (line.includes('req.') || line.includes('params.') || line.includes('body.') || line.includes('query.'))
          ) {
            results.push({
              queryId: 'unsafe-deserialization',
              severity: 'high',
              message: 'Unsafe deserialization detected',
              location: {
                file: file.path,
                line: i + 1,
                column: line.includes('eval(') ? line.indexOf('eval(') + 1 : line.indexOf('JSON.parse(') + 1
              },
              description: 'User input is deserialized without proper validation',
              cwe: 'CWE-502',
              recommendation: 'Validate and sanitize all input before deserialization'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Detect missing CSRF protection
   */
  private static detectMissingCSRF(files: any[]): CodeQLResult[] {
    const results: CodeQLResult[] = [];
    
    for (const file of files) {
      if (file.content && typeof file.content === 'string') {
        const content = file.content;
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          if (
            (line.includes('app.post') || line.includes('app.put') || line.includes('app.delete')) &&
            !line.includes('csrf') && !line.includes('csrfProtection')
          ) {
            results.push({
              queryId: 'missing-csrf',
              severity: 'medium',
              message: 'Missing CSRF protection detected',
              location: {
                file: file.path,
                line: i + 1,
                column: 1
              },
              description: 'State-changing endpoint without CSRF protection',
              cwe: 'CWE-352',
              recommendation: 'Implement CSRF protection for all state-changing endpoints'
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Filter files by programming language
   */
  private static filterFilesByLanguage(files: any[], language: string): any[] {
    const languageExtensions: Record<string, string[]> = {
      javascript: ['.js', '.jsx', '.ts', '.tsx', '.mjs'],
      python: ['.py', '.pyw'],
      java: ['.java'],
      csharp: ['.cs'],
      go: ['.go'],
      php: ['.php'],
      ruby: ['.rb'],
    };
    
    const extensions = languageExtensions[language] || ['.js', '.jsx', '.ts', '.tsx'];
    
    return files.filter(file => 
      file.path && extensions.some(ext => file.path.endsWith(ext))
    );
  }

  /**
   * Analyze CodeQL results and generate summary
   */
  private static analyzeResults(
    results: CodeQLResult[],
    filesScanned: number,
    totalFiles: number,
    startTime: number
  ): CodeQLAnalysis {
    const scanTime = Date.now() - startTime;
    
    const criticalIssues = results.filter(r => r.severity === 'critical').length;
    const highIssues = results.filter(r => r.severity === 'high').length;
    const mediumIssues = results.filter(r => r.severity === 'medium').length;
    const lowIssues = results.filter(r => r.severity === 'low').length;
    const infoIssues = results.filter(r => r.severity === 'info').length;
    
    const recommendations = this.generateRecommendations(results);
    
    return {
      totalIssues: results.length,
      criticalIssues,
      highIssues,
      mediumIssues,
      lowIssues,
      infoIssues,
      results,
      coverage: {
        filesScanned,
        totalFiles,
        languages: ['javascript', 'typescript']
      },
      scanTime,
      recommendations
    };
  }

  /**
   * Generate recommendations based on CodeQL results
   */
  private static generateRecommendations(results: CodeQLResult[]): string[] {
    const recommendations: string[] = [];
    
    const criticalCount = results.filter(r => r.severity === 'critical').length;
    const highCount = results.filter(r => r.severity === 'high').length;
    const mediumCount = results.filter(r => r.severity === 'medium').length;
    
    if (criticalCount > 0) {
      recommendations.push('Address critical security vulnerabilities immediately');
    }
    
    if (highCount > 0) {
      recommendations.push('Review and fix high-severity security issues');
    }
    
    if (mediumCount > 0) {
      recommendations.push('Consider addressing medium-severity security issues');
    }
    
    // Add specific recommendations based on vulnerability types
    const vulnerabilityTypes = new Set(results.map(r => r.queryId));
    
    if (vulnerabilityTypes.has('sql-injection')) {
      recommendations.push('Implement parameterized queries to prevent SQL injection');
    }
    
    if (vulnerabilityTypes.has('xss')) {
      recommendations.push('Sanitize user input and implement Content Security Policy');
    }
    
    if (vulnerabilityTypes.has('hardcoded-credentials')) {
      recommendations.push('Move all secrets to environment variables or secure vaults');
    }
    
    if (vulnerabilityTypes.has('weak-crypto')) {
      recommendations.push('Replace weak cryptographic algorithms with secure alternatives');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('No immediate security issues detected. Continue monitoring with regular scans.');
    }
    
    return recommendations;
  }

  /**
   * Get fallback analysis when CodeQL fails
   */
  private static getFallbackAnalysis(totalFiles: number, startTime: number): CodeQLAnalysis {
    return {
      totalIssues: 0,
      criticalIssues: 0,
      highIssues: 0,
      mediumIssues: 0,
      lowIssues: 0,
      infoIssues: 0,
      results: [],
      coverage: {
        filesScanned: 0,
        totalFiles,
        languages: []
      },
      scanTime: Date.now() - startTime,
      recommendations: [
        'CodeQL analysis could not be completed',
        'Consider running CodeQL locally or using GitHub Advanced Security',
        'Implement basic security scanning as an alternative'
      ]
    };
  }

  /**
   * Convert CodeQL results to SecurityVulnerability format
   */
  static convertToSecurityVulnerabilities(results: CodeQLResult[]): SecurityVulnerability[] {
    return results.map(result => ({
      id: result.queryId,
      severity: result.severity,
      title: result.message,
      description: result.description,
      cve: result.cve,
      cvss: this.getCVSSScore(result.severity),
      affectedFiles: [result.location.file],
      recommendation: result.recommendation,
      category: 'codeql',
      status: 'open'
    }));
  }

  /**
   * Get CVSS score based on severity
   */
  private static getCVSSScore(severity: string): number {
    switch (severity) {
      case 'critical': return 9.0;
      case 'high': return 7.0;
      case 'medium': return 5.0;
      case 'low': return 3.0;
      case 'info': return 1.0;
      default: return 5.0;
    }
  }
} 